import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaMoneyBillWave, FaSearch } from 'react-icons/fa';
import PopupDialog from '../../common/PopupDialog/PopupDialog';
import { BankTransaction } from '../../../models/BankTransaction';
import { CustomerInvoice } from '../../../models/CustomerInvoice';
import { Customer } from '../../../models/Customer';
import { CustomerInvoiceRestDataProvider } from '../../../data/CustomerInvoiceRestDataProvider';
import { CustomerRestDataProvider } from '../../../data/CustomerRestDataProvider';
import { BankTransactionRestDataProvider } from '../../../data/BankTransactionRestDataProvider';

interface Props {
  transaction: BankTransaction;
  onClose: () => void;
  onMatched: (updatedTx: BankTransaction) => void;
}

const BankTransactionPaymentModal: React.FC<Props> = ({ transaction, onClose, onMatched }) => {
  const { t } = useTranslation(['common', 'screens']);

  const invoiceProvider = useMemo(() => new CustomerInvoiceRestDataProvider(), []);
  const customerProvider = useMemo(() => new CustomerRestDataProvider(), []);
  const txProvider = useMemo(() => new BankTransactionRestDataProvider(), []);

  const [invoices, setInvoices] = useState<CustomerInvoice[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Per-invoice credit note tracking — auto-detected from negative totalAmountIncVat
  const [creditNoteIds, setCreditNoteIds] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Load unpaid invoices + customers on mount
  useEffect(() => {
    Promise.all([invoiceProvider.getAll(), customerProvider.getAll()])
      .then(([allInvoices, allCustomers]) => {
        const unpaid = allInvoices.filter(inv => !inv.isPaid);
        setInvoices(unpaid);
        setCustomers(allCustomers);
        // Auto-detect credit notes by negative amount
        setCreditNoteIds(new Set(unpaid.filter(inv => (inv.totalAmountIncVat ?? 0) < 0).map(inv => inv.id)));
      })
      .finally(() => setLoading(false));
  }, [invoiceProvider, customerProvider]);

  const customerName = (customerId: string) =>
    customers.find(c => c.id === customerId)?.name ?? '—';

  // Filter by search term (invoice number or customer name)
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return invoices;
    return invoices.filter(
      inv =>
        inv.invoiceNumber.toLowerCase().includes(q) ||
        customerName(inv.customerId).toLowerCase().includes(q) ||
        inv.reference.toLowerCase().includes(q),
    );
  }, [invoices, search, customers]);

  const toggleCreditNote = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setCreditNoteIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Net effective total: regular invoices add, credit notes reduce.
  const effectiveSelectedTotal = useMemo(
    () =>
      Array.from(selected).reduce((sum, id) => {
        const inv = invoices.find(i => i.id === id);
        const absAmt = Math.abs(inv?.totalAmountIncVat ?? 0);
        return creditNoteIds.has(id) ? sum - absAmt : sum + absAmt;
      }, 0),
    [selected, invoices, creditNoteIds],
  );

  const isBalanced = Math.abs(Math.abs(effectiveSelectedTotal) - transaction.amount) < 0.005;
  const canMatch = isBalanced && selected.size > 0 && !saving;
  const remaining = transaction.amount - Math.abs(effectiveSelectedTotal);

  const toggleInvoice = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleConfirmMatch = async () => {
    setConfirmOpen(false);
    setSaving(true);
    try {
      const paymentDate = transaction.valueDate; // ISO date string "YYYY-MM-DD"
      for (const id of Array.from(selected)) {
        const inv = invoices.find(i => i.id === id)!;
        const isInvCreditNote = creditNoteIds.has(id);
        if (!inv.isBooked) {
          await invoiceProvider.book(id);
        }
        await invoiceProvider.bookPayment(id, paymentDate, isInvCreditNote);
      }
      // Build a ledger reference from the matched invoices and mark as payment-matched
      const ledgerReferenceCode = Array.from(selected)
        .map(id => `CUST${invoices.find(i => i.id === id)?.invoiceNumber ?? ''}`)
        .join(', ');
      const updatedTx: BankTransaction = { ...transaction, isPaymentMatched: true, ledgerReferenceCode };
      await txProvider.update(transaction.id, updatedTx);
      onMatched(updatedTx);
    } catch {
      // Errors surfaced via BaseRestDataProvider toast
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PopupDialog
        open
        title={t('screens:bankTransactions.payment.title')}
        onClose={saving ? () => {} : onClose}
        actions={
          <>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!canMatch}
              onClick={() => setConfirmOpen(true)}
            >
              <FaMoneyBillWave style={{ marginRight: 6 }} />
              {saving
                ? t('screens:bankTransactions.payment.matching')
                : t('screens:bankTransactions.payment.match')}
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary"
              disabled={saving}
              onClick={onClose}
            >
              {t('common:cancel')}
            </button>
          </>
        }
      >
        {/* ── Transaction summary ── */}
        <div className="bank-booking-summary mb-3">
          <div className="bank-booking-summary-row">
            <span className="bank-booking-summary-label">{t('screens:bankTransactions.booking.transactionDate')}</span>
            <span className="bank-booking-summary-value">{transaction.valueDate}</span>
          </div>
          <div className="bank-booking-summary-row">
            <span className="bank-booking-summary-label">{t('screens:bankTransactions.booking.counterparty')}</span>
            <span className="bank-booking-summary-value">
              {transaction.narrativeCounterpartyName || transaction.customerReference || '—'}
            </span>
          </div>
          <div className="bank-booking-summary-row">
            <span className="bank-booking-summary-label">{t('screens:bankTransactions.booking.amount')}</span>
            <span className="bank-booking-summary-value bank-transactions-amount--positive">
              +{transaction.amount.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              {' '}{transaction.currency}
            </span>
          </div>
        </div>

        {/* ── Search ── */}
        <div className="bank-payment-search mb-2">
          <div className="input-group input-group-sm">
            <span className="input-group-text"><FaSearch /></span>
            <input
              type="text"
              className="form-control"
              placeholder={t('screens:bankTransactions.payment.searchPlaceholder')}
              value={search}
              onChange={e => setSearch(e.target.value)}
              disabled={saving}
            />
          </div>
        </div>

        {/* ── Invoice list ── */}
        {loading ? (
          <div className="text-muted small">{t('common:loading')}</div>
        ) : filtered.length === 0 ? (
          <div className="text-muted small">{t('screens:bankTransactions.payment.noInvoices')}</div>
        ) : (
          <div className="bank-payment-invoice-list">
            <table className="table table-sm table-hover mb-0">
              <thead>
                <tr>
                  <th style={{ width: 36 }} />
                  <th style={{ width: 110 }}>{t('screens:bankTransactions.payment.typeColumn')}</th>
                  <th>{t('screens:bankTransactions.payment.invoiceNumber')}</th>
                  <th>{t('screens:bankTransactions.payment.customer')}</th>
                  <th>{t('screens:bankTransactions.payment.invoiceDate')}</th>
                  <th className="text-end">{t('screens:bankTransactions.payment.amount')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(inv => {
                  const isSelected = selected.has(inv.id);
                  const isCN = creditNoteIds.has(inv.id);
                  return (
                    <tr
                      key={inv.id}
                      className={isSelected ? 'bank-payment-invoice-row--selected' : ''}
                      onClick={() => !saving && toggleInvoice(inv.id)}
                      style={{ cursor: saving ? 'default' : 'pointer' }}
                    >
                      <td>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={isSelected}
                          onChange={() => toggleInvoice(inv.id)}
                          disabled={saving}
                          onClick={e => e.stopPropagation()}
                        />
                      </td>
                      <td>
                        <span
                          className={`badge ${isCN ? 'bg-warning text-dark' : 'bg-success'}`}
                          style={{ cursor: saving ? 'default' : 'pointer', userSelect: 'none' }}
                          title={isCN
                            ? t('screens:bankTransactions.payment.toggleToInvoice')
                            : t('screens:bankTransactions.payment.toggleToCreditNote')}
                          onClick={e => !saving && toggleCreditNote(inv.id, e)}
                        >
                          {isCN
                            ? t('screens:bankTransactions.payment.typeCreditNote')
                            : t('screens:bankTransactions.payment.typeInvoice')}
                        </span>
                      </td>
                      <td className="font-monospace">{inv.invoiceNumber || inv.reference || '—'}</td>
                      <td>{customerName(inv.customerId)}</td>
                      <td>{inv.invoiceDate ?? '—'}</td>
                      <td className="text-end">
                        {Math.abs(inv.totalAmountIncVat).toLocaleString('nl-NL', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* ── Totals ── */}
        <div className="bank-payment-totals mt-2">
          <div className="bank-payment-totals-row">
            <span>{t('screens:bankTransactions.payment.selectedTotal')}</span>
            <span className={`fw-bold ${isBalanced ? 'text-success' : selected.size > 0 ? 'text-danger' : ''}`}>
              {Math.abs(effectiveSelectedTotal).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
          {!isBalanced && selected.size > 0 && (
            <div className="bank-payment-totals-row text-danger small">
              <span>{t('screens:bankTransactions.payment.remaining')}</span>
              <span>{Math.abs(remaining).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
          )}
        </div>
      </PopupDialog>

      {/* ── Confirmation ── */}
      {confirmOpen && (
        <PopupDialog
          open
          title={t('screens:bankTransactions.payment.confirmTitle')}
          onClose={() => setConfirmOpen(false)}
          actions={
            <>
              <button type="button" className="btn btn-primary me-2" onClick={handleConfirmMatch}>
                <FaMoneyBillWave style={{ marginRight: 6 }} />
                {t('screens:bankTransactions.payment.confirmMatch')}
              </button>
              <button type="button" className="btn btn-outline-secondary" onClick={() => setConfirmOpen(false)}>
                {t('common:cancel')}
              </button>
            </>
          }
        >
          <p>
            {t('screens:bankTransactions.payment.confirmMessage', {
              count: selected.size,
              amount: Math.abs(effectiveSelectedTotal).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
            })}
          </p>
        </PopupDialog>
      )}
    </>
  );
};

export default BankTransactionPaymentModal;

